// Dialect adapters — FXC-8538 (Warp).
//
// Convert each surface's native graph payload into the canonical
// `Graph`. Inputs are structurally typed (duck-typed) on purpose: this
// package must not depend on Bobbin's or Thread-web's modules, so we
// describe only the fields we read. The full source record is preserved
// on each node/edge's `data` so nothing is lost in translation.

import type { Graph, GraphEdge, GraphNode } from "./types";

/** Minimal shape of a Bobbin spool/Spacewalk node (`DailyGraphNode`). */
export interface SpoolGraphNodeInput {
  id: string;
  concept_type: string;
  label: string;
  [key: string]: unknown;
}

/** Minimal shape of a Bobbin spool/Spacewalk edge (`DailyGraphEdge`). */
export interface SpoolGraphEdgeInput {
  src: string;
  dst: string;
  predicate: string;
  [key: string]: unknown;
}

/** Minimal shape of a Bobbin spool graph response (`DailyGraphResponse`). */
export interface SpoolGraphInput {
  nodes: SpoolGraphNodeInput[];
  edges: SpoolGraphEdgeInput[];
  /** Distinct concept_types — optional; derived if absent. */
  concept_types?: string[];
  /** Distinct edge predicates — optional; derived if absent. */
  edge_predicates?: string[];
}

/**
 * Adapt a Bobbin spool / Spacewalk graph response into the canonical
 * model. `concept_type` → `type`; `child_of` edges are flipped from
 * Bobbin's child → parent shape into canonical parent → child lineage.
 * When the response already carries `concept_types` / `edge_predicates`
 * (Bobbin's does) they are used verbatim; otherwise they are derived.
 */
export function fromSpoolGraph(input: Partial<SpoolGraphInput> | null | undefined): Graph {
  const inputNodes = Array.isArray(input?.nodes) ? input.nodes : [];
  const inputEdges = Array.isArray(input?.edges) ? input.edges : [];
  const nodes: GraphNode[] = inputNodes.map((n) => ({
    id: n.id,
    type: n.concept_type,
    label: n.label,
    data: n,
  }));
  const edges: GraphEdge[] = inputEdges.map((e) => ({
    source: e.predicate === "child_of" ? e.dst : e.src,
    target: e.predicate === "child_of" ? e.src : e.dst,
    predicate: e.predicate,
    data: e,
  }));
  return {
    nodes,
    edges,
    nodeTypes: Array.isArray(input?.concept_types)
      ? input.concept_types
      : distinct(nodes.map((n) => n.type)),
    edgePredicates:
      Array.isArray(input?.edge_predicates)
        ? input.edge_predicates
        : distinct(edges.map((e) => e.predicate)),
  };
}

/** Minimal shape of a Thread-web provenance node (`DAGNode`). */
export interface ProvenanceNodeInput {
  id: string;
  /** Provenance carries both; prefer `type`, fall back to `kind`. */
  type?: string;
  kind?: string;
  /** Human label. */
  name?: string;
  [key: string]: unknown;
}

/** Minimal shape of a Thread-web provenance edge (`DAGEdge`). */
export interface ProvenanceEdgeInput {
  parent_id: string;
  child_id: string;
  /** Prefer `relationship`, fall back to `operation`. */
  relationship?: string;
  operation?: string;
  [key: string]: unknown;
}

/** Minimal shape of a Thread-web provenance graph (`DAGData`). */
export interface ProvenanceGraphInput {
  nodes: ProvenanceNodeInput[];
  edges: ProvenanceEdgeInput[];
}

/**
 * Adapt a Thread-web provenance/artifact DAG into the canonical model.
 * `type ?? kind` → `type`, `name ?? id` → `label`,
 * `parent_id`/`child_id` → `source`/`target`,
 * `relationship ?? operation` → `predicate`.
 */
export function fromProvenanceGraph(input: Partial<ProvenanceGraphInput> | null | undefined): Graph {
  const inputNodes = Array.isArray(input?.nodes) ? input.nodes : [];
  const inputEdges = Array.isArray(input?.edges) ? input.edges : [];
  const nodes: GraphNode[] = inputNodes.map((n) => ({
    id: n.id,
    type: n.type ?? n.kind ?? "node",
    label: n.name ?? n.id,
    data: n,
  }));
  const edges: GraphEdge[] = inputEdges.map((e) => ({
    source: e.parent_id,
    target: e.child_id,
    predicate: e.relationship ?? e.operation ?? "child_of",
    data: e,
  }));
  return {
    nodes,
    edges,
    nodeTypes: distinct(nodes.map((n) => n.type)),
    edgePredicates: distinct(edges.map((e) => e.predicate)),
  };
}

/** Distinct values in first-seen order. */
function distinct(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}
